import { track } from "@plausible-analytics/tracker";
import {
  debounceDeparturesMessages,
  RealtimeModes as modes,
} from "mobility-toolbox-js/ol";
import GeoJSON from "ol/format/GeoJSON";

import { LS_ALL_SITUATIONS_OPEN_KEY } from "../constants";
import tralisAPI from "../TralisAPI";
import { getStationsWithStatus } from "../utils";

import type {
  MaplibreLayer,
  MaplibreStyleLayer,
  MocoLayer,
} from "mobility-toolbox-js/ol";
import type {
  RealtimeDeparture,
  RealtimeLine,
  RealtimeMode,
  RealtimeStation,
  RealtimeStationproperties,
  RealtimeStopSequence,
  RealtimeTrajectory,
  RealtimeTrajectoryProperties,
} from "mobility-toolbox-js/types";
import type { Feature, Map } from "ol";
import type { Geometry } from "ol/geom";

import type TralisLayer from "../TralisLayer";

import type { AppState, Bookmark, Situation } from "./reducers";
import type { AppAction, AppDispatch, AppThunk } from "./store";

/** Signature of `redux-thunk`'s `getState` for this app. */
export type GetState = () => AppState;

/** Signature of `redux-thunk`'s `dispatch` for this app. */
export type Dispatch = AppDispatch;

export const ActionType = {
  ClearDepartures: "CLEAR_DEPARTURES",
  ClearGeofoxDepartures: "CLEAR_GEOFOX_DEPARTURES",
  SetAreSituationsOpen: "SET_ARE_SITUATIONS_OPEN",
  SetBackgroundLayer: "SET_BACKGROUND_LAYER",
  SetBookmarks: "SET_BOOKMARKS",
  SetBookmarksPopup: "SET_BOOKMARKS_POPUP",
  SetDebugMode: "SET_DEBUG_MODE",
  SetDeparturesLoading: "SET_DEPARTURES_LOADING",
  SetGeofoxDeparturesLoading: "SET_GEOFOX_DEPARTURES_LOADING",
  SetHiddenDisruptions: "SET_HIDDEN_DISRUPTIONS",
  SetHighlightStation: "SET_HIGHLIGHT_STATION",
  SetIncidentProgram: "SET_INCIDENT_PROGRAM",
  SetInitialStation: "SET_INITIAL_STATION",
  SetIsFollowingVehicle: "SET_IS_FOLLOWING_VEHICLE",
  SetIsFollowingVehicleLoading: "SET_IS_FOLLOWING_VEHICLE_LOADING",
  SetIsMotsRelationOpened: "SET_IS_MOTS_RELATION_OPENED",
  SetIsRouteInfoOpened: "SET_IS_ROUTE_INFO_OPENED",
  SetMap: "SET_MAP",
  SetMode: "SET_MODE",
  SetNotifications: "SET_NOTIFICATIONS",
  SetNotificationsIconsLayer: "SET_NOTIFICATIONS_ICONS_LAYER",
  SetNotificationsLayer: "SET_NOTIFICATIONS_LAYER",
  SetNotificationsLoading: "SET_NOTIFICATIONS_LOADING",
  SetPanicMode: "SET_PANIC_MODE",
  SetPreviewNotification: "SET_PREVIEW_NOTIFICATION",
  SetPreviewSituations: "SET_PREVIEW_SITUATIONS",
  SetRealtimeLayer: "SET_REALTIME_LAYER",
  SetRisPopup: "SET_RIS_POPUP",
  SetSchematicBgLayer: "SET_SCHEMATIC_BG_LAYER",
  SetSelectedLine: "SET_SELECTED_LINE",
  SetSelectedNotificationsById: "SET_SELECTED_NOTIFICATIONS_BY_ID",
  SetSelectedVehicle: "SET_SELECTED_VEHICLE",
  SetSituations: "SET_SITUATIONS",
  SetStation: "SET_STATION",
  SetStationIconsLegendPopup: "SET_STATION_ICONS_LEGEND_POPUP",
  SetStopSequence: "SET_STOP_SEQUENCE",
  SetStopSequences: "SET_STOP_SEQUENCES",
  SetTopographicBgLayer: "SET_TOPOGRAPHIC_BG_LAYER",
  SetVisibleStations: "SET_VISIBLE_STATIONS",
  TrackEvent: "TRACK_EVENT",
  UpdateBusTrajectories: "UPDATE_BUS_TRAJECTORIES",
  UpdateExtraGeoms: "UPDATE_EXTRA_GEOMS",
  UpdateLineInLines: "UPDATE_LINE_IN_LINES",
  UpdateStationInStations: "UPDATE_STATION_IN_STATIONS",
  UpsertDepartures: "UPSERT_DEPARTURES",
  UpsertDisruptions: "UPSERT_DISRUPTIONS",
  UpsertGeofoxDepartures: "UPSERT_GEOFOX_DEPARTURES",
  UpsertStations: "UPSERT_STATIONS",
} as const;

export type ActionType = (typeof ActionType)[keyof typeof ActionType];

export const SET_PREVIEW_NOTIFICATION = ActionType.SetPreviewNotification;
export const UPSERT_DEPARTURES = ActionType.UpsertDepartures;
export const UPSERT_GEOFOX_DEPARTURES = ActionType.UpsertGeofoxDepartures;
export const UPSERT_DISRUPTIONS = ActionType.UpsertDisruptions;
export const SET_HIDDEN_DISRUPTIONS = ActionType.SetHiddenDisruptions;
export const SET_STATION = ActionType.SetStation;
export const SET_INITIAL_STATION = ActionType.SetInitialStation;
export const UPSERT_STATIONS = ActionType.UpsertStations;
export const UPDATE_STATION_IN_STATIONS = ActionType.UpdateStationInStations;
export const UPDATE_BUS_TRAJECTORIES = ActionType.UpdateBusTrajectories;
export const UPDATE_EXTRA_GEOMS = ActionType.UpdateExtraGeoms;
export const SET_MAP = ActionType.SetMap;
export const SET_VISIBLE_STATIONS = ActionType.SetVisibleStations;
export const SET_MODE = ActionType.SetMode;
export const CLEAR_DEPARTURES = ActionType.ClearDepartures;
export const CLEAR_GEOFOX_DEPARTURES = ActionType.ClearGeofoxDepartures;
export const SET_HIGHLIGHT_STATION = ActionType.SetHighlightStation;
export const SET_INCIDENT_PROGRAM = ActionType.SetIncidentProgram;
export const SET_DEPARTURES_LOADING = ActionType.SetDeparturesLoading;
export const SET_GEOFOX_DEPARTURES_LOADING =
  ActionType.SetGeofoxDeparturesLoading;
export const SET_DEBUG_MODE = ActionType.SetDebugMode;
export const SET_PANIC_MODE = ActionType.SetPanicMode;
export const SET_NOTIFICATIONS = ActionType.SetNotifications;
export const SET_SELECTED_NOTIFICATIONS_BY_ID =
  ActionType.SetSelectedNotificationsById;
export const SET_SELECTED_VEHICLE = ActionType.SetSelectedVehicle;
export const TRACK_EVENT = ActionType.TrackEvent;
export const SET_RIS_POPUP = ActionType.SetRisPopup;
export const SET_IS_ROUTE_INFO_OPENED = ActionType.SetIsRouteInfoOpened;
export const SET_IS_FOLLOWING_VEHICLE = ActionType.SetIsFollowingVehicle;
export const SET_IS_FOLLOWING_VEHICLE_LOADING =
  ActionType.SetIsFollowingVehicleLoading;
export const SET_BOOKMARKS = ActionType.SetBookmarks;
export const SET_BOOKMARKS_POPUP = ActionType.SetBookmarksPopup;
export const UPDATE_LINE_IN_LINES = ActionType.UpdateLineInLines;
export const SET_STOP_SEQUENCE = ActionType.SetStopSequence;
export const SET_STOP_SEQUENCES = ActionType.SetStopSequences;
export const SET_SELECTED_LINE = ActionType.SetSelectedLine;
export const SET_IS_MOTS_RELATION_OPENED = ActionType.SetIsMotsRelationOpened;
export const SET_STATION_ICONS_LEGEND_POPUP =
  ActionType.SetStationIconsLegendPopup;
export const SET_NOTIFICATIONS_LOADING = ActionType.SetNotificationsLoading;
export const SET_SCHEMATIC_BG_LAYER = ActionType.SetSchematicBgLayer;
export const SET_TOPOGRAPHIC_BG_LAYER = ActionType.SetTopographicBgLayer;
export const SET_NOTIFICATIONS_LAYER = ActionType.SetNotificationsLayer;
export const SET_NOTIFICATIONS_ICONS_LAYER =
  ActionType.SetNotificationsIconsLayer;
export const SET_REALTIME_LAYER = ActionType.SetRealtimeLayer;
export const SET_BACKGROUND_LAYER = ActionType.SetBackgroundLayer;
export const SET_SITUATIONS = ActionType.SetSituations;
export const SET_PREVIEW_SITUATIONS = ActionType.SetPreviewSituations;
export const SET_ARE_SITUATIONS_OPEN = ActionType.SetAreSituationsOpen;

const format = new GeoJSON();
let previousDepartureStationId: null | number | undefined = null;

const params = new URLSearchParams(window.location.search);
const notificationat = params.get("notificationat");
// let publicAt = "";
let date;
try {
  date = notificationat ? new Date(notificationat) : new Date();
  // Set seconds and milliseconds to null for better caching
  date.setSeconds(0);
  date.setMilliseconds(0);
  // publicAt = date.toISOString();
} catch (e) {
  // eslint-disable-next-line no-console
  console.error(
    "Invalid notificationat url parameter format, it must be an ISO String.",
    notificationat,
    e,
  );
  // Do nothing if the string is not a valid date
}

export const setTopographicBgLayer = (data: MaplibreLayer): AppAction => {
  return { data, type: SET_TOPOGRAPHIC_BG_LAYER };
};

export const setSchematicBgLayer = (data: MaplibreLayer): AppAction => {
  return { data, type: SET_SCHEMATIC_BG_LAYER };
};

export const setBackgroundLayer = (data: MaplibreLayer): AppAction => {
  return { data, type: SET_BACKGROUND_LAYER };
};

export const setRealtimeLayer = (data: TralisLayer): AppAction => {
  return { data, type: SET_REALTIME_LAYER };
};

export const setNotificationsLayer = (data: MocoLayer): AppAction => {
  return { data, type: SET_NOTIFICATIONS_LAYER };
};

export const setNotificationsIconsLayer = (
  data: MaplibreStyleLayer,
): AppAction => {
  return { data, type: SET_NOTIFICATIONS_ICONS_LAYER };
};

export const setPreviewSituations = (data: Situation[]): AppAction => {
  return { data, type: SET_PREVIEW_SITUATIONS };
};

export const setIncidentProgram = (data: unknown): AppAction => {
  return { data, type: SET_INCIDENT_PROGRAM };
};

export const upsertDepartures = (data: RealtimeDeparture[]): AppAction => {
  return { data, type: UPSERT_DEPARTURES };
};
export const upsertGeofoxDepartures = (data: unknown[]): AppAction => {
  return { data, type: UPSERT_GEOFOX_DEPARTURES };
};

export const setHiddenDisruptions = (data: unknown[]): AppAction => {
  return { data, type: SET_HIDDEN_DISRUPTIONS };
};

export const setStation = (
  station?: Feature<Geometry, RealtimeStationproperties>,
  isStationLoadedFromPermalink = false,
): AppAction => {
  return {
    data: {
      isStationLoadedFromPermalink,
      station,
    },
    type: SET_STATION,
  };
};

export const upsertStations = (
  data: Feature<Geometry, RealtimeStationproperties>[],
): AppAction => {
  return { data, type: UPSERT_STATIONS };
};

export const setVisibleStations = (
  data: Feature<Geometry, RealtimeStationproperties>[],
): AppAction => {
  return { data, type: SET_VISIBLE_STATIONS };
};

export const setMode = (data: RealtimeMode): AppAction => {
  return { data, type: SET_MODE };
};

export const clearDepartures = (): AppAction => {
  return { type: CLEAR_DEPARTURES };
};

export const clearGeofoxDepartures = (): AppAction => {
  return { type: CLEAR_DEPARTURES };
};

export const setHighlightStation = (
  data: Feature<Geometry, RealtimeStationproperties> | null,
): AppAction => {
  return { data, type: SET_HIGHLIGHT_STATION };
};

export const setDeparturesLoading = (data: boolean): AppAction => {
  return { data, type: SET_DEPARTURES_LOADING };
};

export const setGeofoxDeparturesLoading = (data: boolean): AppAction => {
  return { data, type: SET_GEOFOX_DEPARTURES_LOADING };
};

export const setNotificationsLoading = (data: boolean): AppAction => {
  return { data, type: SET_NOTIFICATIONS_LOADING };
};

export const setDebugMode = (data: boolean): AppAction => {
  return { data, type: SET_DEBUG_MODE };
};

export const updateExtraLayerTags = (data: string[]): AppAction => {
  return { data, type: UPDATE_EXTRA_GEOMS };
};

export const updateBusTrajectories = (data: unknown): AppAction => {
  return { data, type: UPDATE_BUS_TRAJECTORIES };
};

export const setPanicMode = (data: boolean): AppAction => {
  return { data, type: SET_PANIC_MODE };
};

export const setMap = (data: Map): AppAction => {
  return { data, type: SET_MAP };
};
export const setSituations = (data: Situation[]): AppAction => {
  return { data, type: SET_SITUATIONS };
};

export const setAreSituationsOpen =
  (data: boolean, saveInLocalStorage: boolean): AppThunk =>
  (dispatch: Dispatch, getState: GetState) => {
    if (saveInLocalStorage) {
      const localStorage = getState().localStorage;
      localStorage.setItem(LS_ALL_SITUATIONS_OPEN_KEY, String(data));
    }
    dispatch({ data, type: SET_ARE_SITUATIONS_OPEN });
  };

export const setPreviewNotification = (data: Notification): AppAction => {
  return { data, type: SET_PREVIEW_NOTIFICATION };
};

export const setNotifications = (data: Notification[] = []): AppAction => {
  return { data, type: SET_NOTIFICATIONS };
};

export const setSelectedNotificationsById = (
  data: string[] = [],
): AppAction => {
  return { data, type: SET_SELECTED_NOTIFICATIONS_BY_ID };
};

export const setIsRouteInfoOpened = (data: boolean): AppAction => {
  return { data, type: SET_IS_ROUTE_INFO_OPENED };
};

export const setIsMotsRelationOpened = (data: boolean): AppAction => {
  return { data, type: SET_IS_MOTS_RELATION_OPENED };
};

export const setIsFollowingVehicle = (data: boolean): AppAction => {
  return { data, type: SET_IS_FOLLOWING_VEHICLE };
};

export const setIsFollowingVehicleLoading = (data: boolean): AppAction => {
  return { data, type: SET_IS_FOLLOWING_VEHICLE_LOADING };
};

export const setSelectedVehicles = (
  selectedVehicles: (RealtimeTrajectory | RealtimeTrajectoryProperties)[] = [],
): AppAction => {
  let dataArray;
  if (selectedVehicles && !Array.isArray(selectedVehicles)) {
    dataArray = [selectedVehicles];
  } else {
    dataArray = selectedVehicles;
  }

  let data = dataArray.map((propsOrGeojsonFeature) => {
    // To be sure we don't break unknownthing after the update to mbt@2.
    const vehicle =
      (propsOrGeojsonFeature as RealtimeTrajectory).properties ||
      propsOrGeojsonFeature;
    return vehicle;
  });

  // For backward compatibility after update to mbt@2 we set an id, to be sure we haven't break unknownthing
  // the id property does not exist unknownmore.
  data.forEach((vehicle: RealtimeTrajectoryProperties) => {
    (
      vehicle as { id: typeof vehicle.train_id } & RealtimeTrajectoryProperties
    ).id = vehicle.train_id;
  });

  // SBAHNMW-891, HACK:
  // Vehicle with no train number returns a stop sequence in the past,
  // so we exclude them from selection.
  // This should be fix in backend.
  data = data.filter((vehicle) => {
    return !!vehicle.train_number;
  });

  return { data, type: SET_SELECTED_VEHICLE };
};

export const setSelectedLine = (data: RealtimeLine): AppAction => {
  return { data, type: SET_SELECTED_LINE };
};

export const setStopSequence = (
  stopSequence: RealtimeStopSequence,
): AppAction => {
  return { data: stopSequence, type: SET_STOP_SEQUENCE };
};
export const setStopSequences = (
  stopSequences?: RealtimeStopSequence[],
): AppAction => {
  return { data: stopSequences, type: SET_STOP_SEQUENCES };
};

export const subscribeExtraLayerTags = (): AppThunk => (dispatch: Dispatch) => {
  // TODO transform tralis api in typescript
  // @ts-expect-error Tralis API is not typed
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  tralisAPI.subscribeExtraLayerTags((data: { content: string[] }) => {
    // All possible values:
    // [
    //   "Hbf Nord",
    //   "Hbf Nord - S7",
    //   "Hbf Nord - S2",
    //   "Hbf Nord Fernbahn",
    //   "Südring - S4",
    //   "Hbf Nord - S4",
    //   "Hbf Nord Fernbahn - S6",
    //   "Hbf Süd - S7",
    //   "Hbf Nord Fernbahn - S1",
    //   "Hbf Süd - S3",
    //   "Moosach - S8",
    //   "Hbf Nord Fernbahn - S8",
    //   "Hbf Gleis 25 Fernbahn",
    //   "Südring - S8",
    //   "Südring - S2",
    //   "Südring - S6",
    //   "Hbf Nord - S3",
    //   "Hbf Süd",
    //   "Heimeranplatz - S2",
    //   "Hbf Nord Fernbahn - S3",
    //   "Heimeranplatz",
    //   "Hbf Süd - S1",
    //   "Hbf Gleis 25 Fernbahn - S1",
    //   "Hbf Nord Fernbahn - S2",
    //   "Südring - S3",
    //   "Südring",
    //   "Moosach",
    //   "Hbf Nord - S1",
    //   "Hbf Nord Fernbahn - S4",
    // ]

    const extraLayerTags: string[] = [];
    const busIds: string[] = [];
    data?.content?.forEach((message) => {
      if (message.startsWith("bus:")) {
        busIds.push(message.split(":")[1]);
      } else {
        extraLayerTags.push(message);
      }
    });
    dispatch(updateExtraLayerTags(extraLayerTags));

    Promise.all(
      busIds.map((id) => tralisAPI.getFullTrajectory(id, "schematic")),
    )
      .then((busTrajectories) => {
        dispatch(updateBusTrajectories(busTrajectories.map((t) => t?.content)));
      })
      .catch((error) => {
        console.warn("Error while fetching bus trajectories", error);
      });
  });
};

export const unsubscribeDepartures =
  (stationId?: number): AppThunk =>
  (dispatch: Dispatch) => {
    tralisAPI.unsubscribeDepartures(stationId || previousDepartureStationId!);
    dispatch(upsertDepartures([]));
  };

export const subscribeDepartures =
  (stationId?: number, sortByMinArrivalTime = false): AppThunk =>
  (dispatch: Dispatch) => {
    dispatch(unsubscribeDepartures());
    dispatch(setDeparturesLoading(!!stationId));
    const onMessage = debounceDeparturesMessages((departures) => {
      dispatch(upsertDepartures(departures || []));
      dispatch(setDeparturesLoading(false));
    }, sortByMinArrivalTime);
    tralisAPI.subscribeDepartures(stationId!, onMessage);
    previousDepartureStationId = stationId;
  };

/**
 *
 * @deprecated
 */
export const subscribeGeofoxDepartures = (
    //stationId?: string
    //
  ): AppThunk =>
  (
    //dispatch: Dispatch
  ) => {
    // Geofox has been removed in backend
    // dispatch(setGeofoxDeparturesLoading(!!stationId));
    // tralisAPI.subscribeGeofoxDepartures(stationId, (departures) => {
    //   dispatch(upsertGeofoxDepartures(departures || {}));
    //   dispatch(setGeofoxDeparturesLoading(false));
    // });
  };
/**
 *
 * @deprecated
 */
export const unsubscribeGeofoxDepartures = (
    //stationId?: string
  ): AppThunk =>
  (
    //dispatch, getState
  ) => {
    // Geofox has been removed in backend
    // const { tenant } = getState().config;
    // tralisAPI.unsubscribeGeofoxDepartures(stationId, tenant);
  };

/**
 * Subscribe to the disruptions channel.
 * @deprecated
 */
export const subscribeDisruptions =
  (): AppThunk =>
  (
    //dispatch, getState
  ) => {
    // const { config } = getState();
    // tralisAPI.subscribeDisruptions(config.tenant, (data) => {
    //   if (data && data.content && data.content.messages) {
    //     dispatch(upsertDisruptions(data.content.messages));
    //     dispatch(setIncidentProgram(data.content.incident_program));
    //   }
    // });
  };

/**
 * Subscribe to the stopSequence channel.
 */
export const subscribeStopSequence =
  (vehicleId: string): AppThunk =>
  (dispatch: Dispatch) => {
    tralisAPI.unsubscribeStopSequence(vehicleId);
    tralisAPI.subscribeStopSequence(vehicleId, (data) => {
      const stopSequences = data.content;
      if (!stopSequences) {
        dispatch(setStopSequences());
      } else {
        const seqs: ({ stationsWithStatus: unknown } & RealtimeStopSequence)[] =
          stopSequences.map((stopSequence) => {
            return {
              ...stopSequence,
              stationsWithStatus: getStationsWithStatus(
                stopSequence,
              ) as unknown,
            };
          });
        // Remove stopsequence with the same destination.
        // It happens when 2 trains are merged in a common station.
        const uniqueSeqForDestination: ({
          stationsWithStatus: unknown;
        } & RealtimeStopSequence)[] = [];
        seqs.forEach((seq) => {
          if (
            !uniqueSeqForDestination.find(
              (uniq) => uniq.destination === seq.destination,
            )
          ) {
            uniqueSeqForDestination.push(seq);
          }
        });
        dispatch(setStopSequences(uniqueSeqForDestination));
      }
    });
  };

export const unsubscribeStopSequence =
  (vehicleId: string): AppThunk =>
  (dispatch: Dispatch) => {
    tralisAPI.unsubscribeStopSequence(vehicleId);
    dispatch(setStopSequences());
  };

/**
 * Subscribe to the stations channel then update the stations array with the updated station.
 */
let stationsTimeout: null | number = null;
export const subscribeStations =
  (): AppThunk => (dispatch: Dispatch, getState: GetState) => {
    const { mode } = getState();
    tralisAPI.unsubscribeStations();
    dispatch(upsertStations([]));
    let stations: RealtimeStation[] = [];
    tralisAPI.subscribeStations(mode, (data) => {
      const geojson = data.content;
      if (!geojson) {
        return;
      }
      if (stationsTimeout) {
        window.clearTimeout(stationsTimeout);
      }
      stations.push(geojson);
      stationsTimeout = window.setTimeout(() => {
        let features = format.readFeatures({
          features: stations,
          type: "FeatureCollection",
        });
        stations = [];
        // [SBAHNMW-1209] Filter out replacement stations in topographic mode,
        // as they are not displayed on the map. And breaks the clickability
        // of a station.
        if (mode === modes.TOPOGRAPHIC) {
          features = features.filter((feature) => {
            return !feature.get("isRailReplacement");
          });
        }
        dispatch({
          data: features,
          type: UPDATE_STATION_IN_STATIONS,
        });
      }, 300);
    });
  };

/**
 * Unubscribe to the stations channel.
 */
export const unsubscribeStations = (): AppThunk => () => {
  if (stationsTimeout) {
    window.clearTimeout(stationsTimeout);
  }
  tralisAPI.unsubscribeStations();
};

/**
 * Subscribe to the deleted_vehicles channel to remove the deleted_vehicle from the selected vehicles array.
 */
let deletedVehiclesCb: ((data: { content: string }) => void) | null = null;
export const subscribeDeletedVehicles =
  (): AppThunk => (dispatch: Dispatch, getState: GetState) => {
    if (deletedVehiclesCb) {
      tralisAPI.unsubscribeDeletedVehicles(deletedVehiclesCb);
    }
    const { mode } = getState();
    deletedVehiclesCb = (data) => {
      const { selectedVehicles } = getState();
      const index = selectedVehicles.findIndex(
        ({ train_id: id }) => data.content === id,
      );

      if (index !== -1) {
        selectedVehicles.splice(index, 1);
        dispatch({
          data: [...selectedVehicles],
          type: SET_SELECTED_VEHICLE,
        });
      }
    };
    tralisAPI.subscribeDeletedVehicles(mode, deletedVehiclesCb);
  };

export const updateStation =
  (stationId: number, mode: RealtimeMode, showDepartures = false): AppThunk =>
  (dispatch: Dispatch, getState: GetState, extraArgs: unknown) => {
    // If api is closed or closing we try to update the station after 1s
    if (tralisAPI.wsApi.closing || tralisAPI.wsApi.closed) {
      window.setTimeout(() => {
        updateStation(stationId, mode, showDepartures)(
          dispatch,
          getState,
          extraArgs,
        );
      }, 1000);
      return;
    }

    tralisAPI
      .getStation(stationId, mode)
      .then((data) => {
        const geojson = data.content;
        if (!geojson) {
          return;
        }
        const feature = format.readFeature(geojson) as Feature<
          Geometry,
          RealtimeStationproperties
        >;
        feature.set("mode", mode);
        dispatch(setStation(feature, !showDepartures));
      })
      .catch((error: { type: string }) => {
        if (error.type === "close") {
          // when the connection is closed during a request (switching mode for example) we don't log the error.
          return;
        }
        console.error("Error in updateStation", error);
      });
  };

export const updateStationAndHighlight =
  (stationId: number, mode: RealtimeMode): AppThunk =>
  (dispatch: Dispatch) => {
    tralisAPI
      .getStation(stationId, mode)
      .then((data) => {
        const geojson = data.content;
        if (!geojson) {
          return;
        }
        const feature = format.readFeature(geojson) as Feature<
          Geometry,
          RealtimeStationproperties
        >;
        feature.set("mode", mode);
        feature.set("animationDuration", 0);
        dispatch(setStation(feature));
        dispatch(setHighlightStation(feature));
      })
      .catch((error: { type: string }) => {
        if (error.type === "close") {
          // when the connection is closed during a request (switching mode for example) we don't log the error.
          return;
        }
      });
  };

/**
 * Get lines to the lines channel.
 */
export const getLines = (): AppThunk => (dispatch: Dispatch) => {
  // @ts-expect-error Tralis api has not types yet
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  tralisAPI.subscribeLines((line: RealtimeLine) => {
    dispatch({
      data: line,
      type: UPDATE_LINE_IN_LINES,
    });
  });
};

export const trackEvent = (
  category: string,
  action: string,
  name: string,
  value?: string,
): AppAction => {
  if (window._paq) {
    // track events only on production (see /public/index.html)

    window._paq.push(["trackEvent", category, action, name, value]);
  }
  if (window.plausible) {
    // track events only on production (see /public/index.html)
    // @ts-expect-error - plausible has not types for trackEvent
    track("trackEvent", { props: { action, category, name, value } });
  }

  return {
    data: `${category} ${action} ${name} ${value}`,
    type: TRACK_EVENT,
  };
};

export const setRisPopup = (data: unknown): AppAction => {
  return { data, type: SET_RIS_POPUP };
};

export const setStationIconsLegendPopup = (data: unknown): AppAction => {
  return { data, type: SET_STATION_ICONS_LEGEND_POPUP };
};

export const setBookmarksPopup = (data: unknown): AppAction => {
  return { data, type: SET_BOOKMARKS_POPUP };
};

export const setBookmark =
  (data: Bookmark, index: number): AppThunk =>
  (dispatch: Dispatch, getState: GetState) => {
    const { bookmarks } = getState();
    bookmarks[index] = data;
    dispatch({
      data: [...bookmarks],
      type: SET_BOOKMARKS,
    });
  };

export const addBookmark =
  (data: Bookmark): AppThunk =>
  (dispatch: Dispatch, getState: GetState) => {
    const { bookmarks } = getState();

    dispatch({
      data: [data, ...bookmarks],
      type: SET_BOOKMARKS,
    });
  };

export const removeBookmark =
  (data: Bookmark): AppThunk =>
  (dispatch: Dispatch, getState: GetState) => {
    const { bookmarks } = getState();
    const index = bookmarks.indexOf(data);
    if (index !== -1) {
      bookmarks.splice(index, 1);
      dispatch({
        data: [...bookmarks],
        type: SET_BOOKMARKS,
      });
    }
  };
